/**
 * AuditLogFilters — filter bar for the audit trail page.
 * Supports: event_type multi-select, date range, job_id search, free-text.
 * US-008 Step 2 — FR-074 (searchable audit trail).
 * WCAG 2.1 AA: all inputs are labelled, keyboard accessible.
 */
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const AUDIT_EVENT_TYPES = [
  'job.submitted',
  'job.cancelled',
  'job.claimed',
  'job.resubmitted',
  'pipeline.started',
  'pipeline.completed',
  'pipeline.failed',
  'agent.started',
  'agent.completed',
  'agent.failed',
  'human_review.decision',
  'kb.document.uploaded',
  'kb.document.deleted',
  'kb.snapshot',
  'gate_rules.created',
  'gate_rules.updated',
  'gate_rules.deleted',
  'gate_rules.toggled',
  'audit.exported',
  'danger_zone.deleted',
] as const;

export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

export interface AuditFilters {
  eventTypes: AuditEventType[];
  dateFrom: string;
  dateTo: string;
  jobId: string;
  search: string;
}

interface AuditLogFiltersProps {
  filters: AuditFilters;
  onChange: (filters: AuditFilters) => void;
  className?: string;
}

const INPUT_CLASS =
  'h-9 rounded-md border border-input bg-background px-3 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 text-foreground placeholder:text-muted-foreground';

export function AuditLogFilters({ filters, onChange, className }: AuditLogFiltersProps) {
  const hasActiveFilters =
    filters.eventTypes.length > 0 ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.jobId !== '' ||
    filters.search !== '';

  const update = (patch: Partial<AuditFilters>) => { onChange({ ...filters, ...patch }); };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions).map(
      (o) => o.value as AuditEventType,
    );
    update({ eventTypes: selected });
  };

  const clearAll = () =>
    { onChange({ eventTypes: [], dateFrom: '', dateTo: '', jobId: '', search: '' }); };

  return (
    <div className={cn('flex flex-wrap items-end gap-3', className)} role="search" aria-label="Audit log filters">
      {/* Free-text search */}
      <div className="flex flex-col gap-1">
        <label htmlFor="audit-search" className="text-xs font-medium text-muted-foreground">
          Search
        </label>
        <input
          id="audit-search"
          type="search"
          value={filters.search}
          onChange={(e) => { update({ search: e.target.value }); }}
          placeholder="Actor, metadata keyword..."
          className={cn(INPUT_CLASS, 'w-52')}
        />
      </div>

      {/* Job ID filter */}
      <div className="flex flex-col gap-1">
        <label htmlFor="audit-job-id" className="text-xs font-medium text-muted-foreground">
          Job ID
        </label>
        <input
          id="audit-job-id"
          type="text"
          value={filters.jobId}
          onChange={(e) => { update({ jobId: e.target.value }); }}
          placeholder="Search by Job ID..."
          className={cn(INPUT_CLASS, 'w-52')}
        />
      </div>

      {/* Event type select (multi) */}
      <div className="flex flex-col gap-1">
        <label htmlFor="audit-event-type" className="text-xs font-medium text-muted-foreground">
          Event type
        </label>
        <select
          id="audit-event-type"
          multiple
          value={filters.eventTypes}
          onChange={handleEventTypeChange}
          aria-label="Filter by event type — hold Ctrl/Cmd for multiple"
          className={cn(INPUT_CLASS, 'h-auto py-1 w-48 min-h-9')}
          size={1}
        >
          {AUDIT_EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Date range */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Date from</span>
        <input
          id="audit-date-from"
          type="date"
          value={filters.dateFrom}
          aria-label="Filter from date"
          onChange={(e) => { update({ dateFrom: e.target.value }); }}
          className={cn(INPUT_CLASS, 'w-40')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Date to</span>
        <input
          id="audit-date-to"
          type="date"
          value={filters.dateTo}
          min={filters.dateFrom || undefined}
          aria-label="Filter to date"
          onChange={(e) => { update({ dateTo: e.target.value }); }}
          className={cn(INPUT_CLASS, 'w-40')}
        />
      </div>

      {/* Clear button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAll}
          aria-label="Clear all filters"
          className="self-end"
        >
          <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
